import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, UploadCloud, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyTrack = () => ({ title: "", version: "", primary_artist: "", featuring_artist: "", isrc: "", composer: "", lyricist: "", producer: "" });

export default function SubmitRelease() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [artists, setArtists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", release_type: "Single", artist_id: "", genre: "", language: "English",
    release_date: "", copyright_owner: "", label_id: "", upc_ean: "",
  });
  const [tracks, setTracks] = useState([emptyTrack()]);
  const [artwork, setArtwork] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => {
      setArtists(r.data);
      if (user.role === "artist" && r.data[0]) setForm((f) => ({ ...f, artist_id: r.data[0].id }));
    });
    api.get("/labels").then((r) => setLabels(r.data));
  }, [user.role]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updTrack = (i, k, v) => setTracks((t) => t.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const submit = async (doSubmit) => {
    if (!form.title || !form.artist_id) return toast.error("Title and Artist are required");
    if (!tracks[0].title) return toast.error("At least one track title is required");
    setBusy(true);
    try {
      const { data: rel } = await api.post("/releases", { ...form, tracks, submit: false });
      if (artwork) {
        const fd = new FormData();
        fd.append("file", artwork);
        fd.append("file_type", "artwork");
        await api.post(`/releases/${rel.id}/files`, fd);
      }
      for (const af of audioFiles) {
        const fd = new FormData();
        fd.append("file", af);
        fd.append("file_type", "audio");
        await api.post(`/releases/${rel.id}/files`, fd);
      }
      if (doSubmit) await api.post(`/releases/${rel.id}/submit`);
      toast.success(doSubmit ? "Release submitted for review!" : "Draft saved");
      nav(`/releases/${rel.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save release");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="submit-release-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Submit Release</h1>
        <p className="mt-1 text-muted-foreground">Provide metadata, tracks, and upload your files.</p>
      </div>

      <Card className="space-y-4 p-6">
        <h3 className="font-heading text-lg font-semibold">Release Details</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Release Title *</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="rel-title" className="mt-1" /></div>
          <div>
            <Label>Release Type</Label>
            <Select value={form.release_type} onValueChange={(v) => upd("release_type", v)}>
              <SelectTrigger className="mt-1" data-testid="rel-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["Single", "EP", "Album"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Artist *</Label>
            <Select value={form.artist_id} onValueChange={(v) => upd("artist_id", v)} disabled={user.role === "artist"}>
              <SelectTrigger className="mt-1" data-testid="rel-artist"><SelectValue placeholder="Select artist" /></SelectTrigger>
              <SelectContent>{artists.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Label</Label>
            <Select value={form.label_id} onValueChange={(v) => upd("label_id", v)}>
              <SelectTrigger className="mt-1" data-testid="rel-label"><SelectValue placeholder="Select label" /></SelectTrigger>
              <SelectContent>{labels.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Genre</Label><Input value={form.genre} onChange={(e) => upd("genre", e.target.value)} data-testid="rel-genre" className="mt-1" /></div>
          <div><Label>Language</Label><Input value={form.language} onChange={(e) => upd("language", e.target.value)} data-testid="rel-language" className="mt-1" /></div>
          <div><Label>Release Date</Label><Input type="date" value={form.release_date} onChange={(e) => upd("release_date", e.target.value)} data-testid="rel-date" className="mt-1" /></div>
          <div><Label>Copyright Owner</Label><Input value={form.copyright_owner} onChange={(e) => upd("copyright_owner", e.target.value)} data-testid="rel-copyright" className="mt-1" /></div>
          <div><Label>UPC/EAN (optional)</Label><Input value={form.upc_ean} onChange={(e) => upd("upc_ean", e.target.value)} data-testid="rel-upc" className="mt-1" /></div>
        </div>
      </Card>

      <Card className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Tracks</h3>
          <Button variant="secondary" size="sm" onClick={() => setTracks([...tracks, emptyTrack()])} data-testid="add-track-btn"><Plus className="mr-1 h-4 w-4" />Add Track</Button>
        </div>
        {tracks.map((t, i) => (
          <div key={i} className="rounded-lg border border-border p-4" data-testid={`track-${i}`}>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold">Track {i + 1}</p>
              {tracks.length > 1 && <Button variant="ghost" size="sm" onClick={() => setTracks(tracks.filter((_, j) => j !== i))} data-testid={`remove-track-${i}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div><Label className="text-xs">Track Title *</Label><Input value={t.title} onChange={(e) => updTrack(i, "title", e.target.value)} data-testid={`track-title-${i}`} className="mt-1" /></div>
              <div><Label className="text-xs">Version</Label><Input value={t.version} onChange={(e) => updTrack(i, "version", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Primary Artist</Label><Input value={t.primary_artist} onChange={(e) => updTrack(i, "primary_artist", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Featuring</Label><Input value={t.featuring_artist} onChange={(e) => updTrack(i, "featuring_artist", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">ISRC</Label><Input value={t.isrc} onChange={(e) => updTrack(i, "isrc", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Composer</Label><Input value={t.composer} onChange={(e) => updTrack(i, "composer", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Lyricist</Label><Input value={t.lyricist} onChange={(e) => updTrack(i, "lyricist", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Producer</Label><Input value={t.producer} onChange={(e) => updTrack(i, "producer", e.target.value)} className="mt-1" /></div>
            </div>
          </div>
        ))}
      </Card>

      <div className="grid gap-6 sm:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-3 font-heading text-lg font-semibold">Artwork (JPG/PNG)</h3>
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border p-6 hover:border-primary/50" data-testid="artwork-drop">
            {artwork ? (
              <img src={URL.createObjectURL(artwork)} alt="preview" className="h-32 w-32 rounded object-cover" />
            ) : (
              <><ImageIcon className="h-8 w-8 text-muted-foreground" /><p className="mt-2 text-sm text-muted-foreground">Click to select artwork</p></>
            )}
            <input type="file" accept=".jpg,.jpeg,.png" className="hidden" onChange={(e) => setArtwork(e.target.files[0])} data-testid="artwork-input" />
          </label>
          {artwork && <p className="mt-2 text-xs text-muted-foreground">{artwork.name}</p>}
        </Card>

        <Card className="p-6">
          <h3 className="mb-3 font-heading text-lg font-semibold">Audio (WAV/FLAC)</h3>
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border p-6 hover:border-primary/50" data-testid="audio-drop">
            <UploadCloud className="h-8 w-8 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">Click to select audio files</p>
            <input type="file" accept=".wav,.flac" multiple className="hidden" onChange={(e) => setAudioFiles([...e.target.files])} data-testid="audio-input" />
          </label>
          {audioFiles.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              {audioFiles.map((f, i) => <li key={i}>{f.name}</li>)}
            </ul>
          )}
        </Card>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={() => submit(false)} disabled={busy} data-testid="save-draft-btn">Save Draft</Button>
        <Button onClick={() => submit(true)} disabled={busy} data-testid="submit-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Submit for Review
        </Button>
      </div>
    </div>
  );
}
